import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swords, Cat, SkipForward, Crown } from 'lucide-react';
import { TEAM_META } from '../../data/teams';
import { burstConfetti } from '../../utils/confetti';
import {
  swingPeriodAt, swingOffset, phaseStep, finalePosition, signOf, sideOf,
  FINALE, FINALE_LANDED_MS,
} from '../../utils/draft';

// Same mapping as the Team Games page. Duplicated rather than exported from
// data/teams.js so that module stays free of UI imports.
const TEAM_ICONS = { vikings: Swords, panthers: Cat };

// How far the card travels either side of centre, in vw. Tuned so it clearly
// overlaps each team zone without leaving the viewport on a laptop.
const TRAVEL_VW = 27;

// The ceremony runs itself: each name swings for this long and then locks in on
// its own, so nobody has to touch the screen. Tapping or pressing SPACE still
// locks the current name in early for a host who wants to drive the pace.
const AUTO_LOCK_MS = 1800;

/**
 * The host-controlled draft ceremony.
 *
 * Teams are already decided before this mounts — `queue` carries each player
 * with the team they were dealt. Nothing here influences the outcome: the
 * swinging, the near-miss and the slam are pure theatre over a settled result.
 *
 * Card position is written straight to the DOM from a rAF loop rather than
 * through React state. At 60fps a setState per frame would re-render the whole
 * overlay — rosters, queue strip and all — sixty times a second.
 */
export default function TeamDraft({ queue, captains = {}, onDone, onSkip }) {
  const [index, setIndex] = useState(0);
  const [landed, setLanded] = useState({ vikings: [], panthers: [] });
  // 'swing' while waiting on the host, 'finale' once they have locked in.
  const [stage, setStage] = useState('swing');

  const cardRef = useRef(null);
  const zoneRefs = useRef({ vikings: null, panthers: null });
  const rafRef = useRef(0);
  const phaseRef = useRef(0);
  const lastTsRef = useRef(0);
  const heldRef = useRef(0);
  const xRef = useRef(0);
  // Position and timestamp at lock-in, so the finale glides from where the card
  // actually was instead of teleporting.
  const lockXRef = useRef(0);
  const lockTsRef = useRef(0);
  const stageRef = useRef('swing');
  const advancedRef = useRef(false);
  const settleTimerRef = useRef(0);

  // The settle timer can outlive the overlay if the host skips mid-slam.
  useEffect(() => () => window.clearTimeout(settleTimerRef.current), []);

  const current = queue[index] || null;
  const total = queue.length;
  const remaining = total - index - 1;

  // Paint the card and light up whichever zone it is currently over.
  const paint = useCallback((x, opts = {}) => {
    const card = cardRef.current;
    if (!card) return;
    const side = sideOf(x);
    const meta = TEAM_META[side];
    const tilt = x * 7;
    const lift = opts.slammed ? 1.06 : 1 - Math.abs(x) * 0.04;
    card.style.transform = `translateX(${x * TRAVEL_VW}vw) rotate(${tilt}deg) scale(${lift})`;
    card.style.borderColor = meta.color;
    card.style.background = opts.slammed ? meta.color : 'rgba(255,255,255,0.92)';
    card.style.color = opts.slammed ? '#fff' : '#0f172a';
    card.style.boxShadow = `0 18px 60px ${meta.color}55`;

    for (const id of Object.keys(zoneRefs.current)) {
      const el = zoneRefs.current[id];
      if (!el) continue;
      const hot = id === side;
      el.style.transform = hot ? 'scale(1.04)' : 'scale(1)';
      el.style.boxShadow = hot
        ? `0 0 0 3px ${TEAM_META[id].color}, 0 0 46px ${TEAM_META[id].color}66`
        : 'none';
    }
  }, []);

  // Commit the current player onto their team, then queue the next one.
  const finish = useCallback(() => {
    if (advancedRef.current || !current) return;
    advancedRef.current = true;
    setLanded((prev) => ({
      ...prev,
      [current.teamId]: [...prev[current.teamId], current.member],
    }));
    burstConfetti({ colors: [TEAM_META[current.teamId].color, '#ffffff'], count: 34 });

    // Flare the winning zone instead of shaking the screen. Re-adding the class
    // needs a reflow between remove and add, or the animation will not restart
    // on the second player to land on the same side.
    const zone = zoneRefs.current[current.teamId];
    if (zone) {
      zone.classList.remove('tg-zone-pulse');
      void zone.offsetWidth;
      zone.classList.add('tg-zone-pulse');
    }

    const isLast = index >= total - 1;
    settleTimerRef.current = window.setTimeout(() => {
      if (isLast) onDone();
      else { setIndex((i) => i + 1); setStage('swing'); }
    }, FINALE.settle);
  }, [current, index, total, onDone]);

  // Reset per-player animation state whenever a new player steps up.
  useEffect(() => {
    phaseRef.current = 0;
    heldRef.current = 0;
    lastTsRef.current = 0;
    xRef.current = 0;
    advancedRef.current = false;
    stageRef.current = 'swing';
  }, [index]);

  useEffect(() => { stageRef.current = stage; }, [stage]);

  // One rAF for the whole ceremony — it reads refs, so it never needs tearing
  // down and rebuilding between players.
  useEffect(() => {
    if (!current) return undefined;

    const tick = (ts) => {
      rafRef.current = requestAnimationFrame(tick);
      if (!lastTsRef.current) { lastTsRef.current = ts; return; }
      // Clamp the delta so a backgrounded tab does not fast-forward the swing.
      const delta = Math.min(64, ts - lastTsRef.current);
      lastTsRef.current = ts;

      if (stageRef.current === 'swing') {
        heldRef.current += delta;
        phaseRef.current += phaseStep(delta, swingPeriodAt(heldRef.current));
        const x = swingOffset(phaseRef.current);
        xRef.current = x;
        paint(x);
        return;
      }

      const elapsed = ts - lockTsRef.current;
      const { x, stage: beat } = finalePosition(elapsed, lockXRef.current, signOf(current.teamId));
      xRef.current = x;
      paint(x, { slammed: beat === 'slam' || beat === 'landed' });
      if (elapsed >= FINALE_LANDED_MS) finish();
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [current, paint, finish]);

  // Host locks the current player in.
  const lockIn = useCallback(() => {
    if (stageRef.current !== 'swing' || !current) return;
    lockXRef.current = xRef.current;
    lockTsRef.current = performance.now();
    stageRef.current = 'finale';
    setStage('finale');
  }, [current]);

  // Auto-advance: lock the current name in once its swing has had its moment.
  // Re-armed per player because `index` (and therefore `lockIn`) changes.
  useEffect(() => {
    if (stage !== 'swing' || !current) return undefined;
    const timer = window.setTimeout(lockIn, AUTO_LOCK_MS);
    return () => window.clearTimeout(timer);
  }, [stage, current, lockIn]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        e.preventDefault();
        lockIn();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lockIn, onSkip]);

  if (!current) return null;

  const upNext = queue.slice(index + 1, index + 5).map((entry) => entry.member.name);

  const zoneProps = (id) => ({
    id,
    roster: landed[id],
    // A team with one slot left makes the remaining picks a foregone
    // conclusion — badge it and let the room feel the squeeze.
    finalSpot: queue.filter((entry) => entry.teamId === id).length - landed[id].length === 1,
    captain: captains[id] || null,
    zoneRef: (el) => { zoneRefs.current[id] = el; },
  });

  return (
    <div
      role="presentation"
      onClick={lockIn}
      className="fixed inset-0 z-[90] flex flex-col"
      style={{ background: 'rgba(8,12,24,0.94)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      {/* Progress + escape hatch */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-8 pt-4 sm:pt-6 shrink-0">
        <div className="text-white/70 text-xs sm:text-sm font-bold uppercase tracking-[0.25em]">
          Pick {index + 1} of {total}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSkip(); }}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-white border border-white/25 hover:border-white/60 rounded-lg px-3 py-1.5 transition"
        >
          <SkipForward size={13} /> Skip all
        </button>
      </div>

      {/* Arena: Vikings zone · swinging card · Panthers zone */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-3 sm:px-8">
        <div className="w-full max-w-6xl grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-6 items-start">
          <TeamZone {...zoneProps('vikings')} />
          <DraftCard cardRef={cardRef} name={current.member.name} />
          <TeamZone {...zoneProps('panthers')} />
        </div>
      </div>

      {/* Prompt + queue */}
      <div className="shrink-0 px-4 sm:px-8 pb-6 sm:pb-8 text-center space-y-3">
        {stage === 'swing' ? (
          <p className="tg-draft-prompt text-white font-bold text-sm sm:text-lg">
            Deciding…
            <span className="block text-white/50 text-[11px] sm:text-xs font-semibold mt-1">
              tap or press <kbd className="px-1.5 py-0.5 rounded bg-white/15 border border-white/25 font-mono">SPACE</kbd> to lock in early
            </span>
          </p>
        ) : (
          <p className="text-white/50 font-bold text-sm sm:text-lg">Locked in!</p>
        )}
        {upNext.length > 0 && (
          <p className="text-white/45 text-xs sm:text-sm">
            <span className="font-bold uppercase tracking-widest mr-2">Up next</span>
            {upNext.join(' · ')}
            {remaining > upNext.length ? ` · +${remaining - upNext.length}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

// One side of the arena: the team banner plus the roster it has claimed so far.
function TeamZone({ id, roster, finalSpot, captain, zoneRef }) {
  const meta = TEAM_META[id];
  const Icon = TEAM_ICONS[id];
  return (
    <div className="min-w-0">
      <div
        ref={zoneRef}
        className="rounded-2xl border-2 px-3 sm:px-4 py-3 transition-all duration-200"
        style={{ borderColor: meta.color, background: `${meta.color}1f` }}
      >
        <div className="flex items-center gap-2 justify-center">
          <Icon size={20} style={{ color: '#fff' }} className="shrink-0" />
          <span className="font-display font-extrabold text-white text-sm sm:text-xl truncate">{meta.short}</span>
          <span
            className="ml-1 text-xs sm:text-sm font-bold text-white rounded-full px-2 py-0.5 shrink-0"
            style={{ background: meta.color }}
          >
            {roster.length}
          </span>
        </div>
        {captain && (
          <div className="mt-1.5 flex items-center justify-center gap-1 text-[10px] sm:text-xs font-semibold text-white/70">
            <Crown size={11} className="text-amber-300 shrink-0" />
            <span className="truncate">Captain {captain.name}</span>
          </div>
        )}
        {finalSpot && (
          <div className="mt-1.5 text-center text-[10px] sm:text-xs font-bold uppercase tracking-widest text-amber-300 tg-final-spot">
            Final spot
          </div>
        )}
      </div>

      <ul className="mt-3 space-y-1.5 max-h-[38vh] overflow-y-auto pr-1">
        {roster.map((member) => (
          <li
            key={member.id}
            className="tg-draft-chip flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-white/10 border border-white/10"
          >
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
              style={{ background: meta.color }}
            >
              {(member.name || '?').trim().charAt(0).toUpperCase()}
            </span>
            <span className="text-white/90 text-xs sm:text-sm font-semibold truncate">{member.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The swinging name card. Split out so its ref stays stable while the
// surrounding rosters re-render.
function DraftCard({ cardRef, name }) {
  return (
    <div className="flex items-center justify-center px-1 sm:px-4 pt-6 sm:pt-10">
      <div
        ref={cardRef}
        className="rounded-2xl border-4 px-4 sm:px-8 py-4 sm:py-6 text-center will-change-transform"
        style={{ background: 'rgba(255,255,255,0.92)', borderColor: '#94a3b8' }}
      >
        <div
          className="font-display font-extrabold leading-none whitespace-nowrap"
          style={{ fontSize: 'clamp(1.35rem, 5.5vw, 3.25rem)' }}
        >
          {name}
        </div>
      </div>
    </div>
  );
}
